/**
 * @file `SettingRow` — one settings key rendered as a row.
 * @module @labcraft/admin/ui/settings/setting-row
 *
 * Owns per-key UI state (draft value, dirty flag, history expander).
 * Save sends the draft via `useSetSettingMutation`; on success the
 * mutation invalidates the `['admin','settings']` prefix, the panel
 * re-fetches `/values`, and `currentValue` advances on the next
 * render.
 *
 * History is loaded lazily by `useSettingHistory(daemon, key, enabled)`:
 * the hook only fires its query once the operator expands the row.
 *
 * Phase 9 of umbrella #406: replaced the `useState +
 * service.settings.history()` snapshot + the parent-callback
 * `onSaved` patching with hook-driven reads and mutation
 * invalidation.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type JSX,
} from 'react'
import type { SettingDefinition } from '@labcraft/postgres/shared'
import { useSetSettingMutation, useSettingHistory } from '../hooks/index.ts'

/** Props for {@link SettingRow}. */
export type SettingRowProps = {
  daemon: string
  def: SettingDefinition
  currentValue: unknown
}

/** Render one settings row. */
export function SettingRow({
  daemon,
  def,
  currentValue,
}: SettingRowProps): JSX.Element {
  const [draft, setDraft] = useState<unknown>(currentValue)
  const [expanded, setExpanded] = useState(false)
  const controlId = useId()

  const setSetting = useSetSettingMutation()
  const history = useSettingHistory(daemon, def.key, expanded)

  // Server's currentValue changed (own save propagated via
  // invalidation, or someone else's flip arrived via the next /values
  // refetch) — sync the draft so we don't show a stale local edit on
  // top.
  useEffect(() => {
    setDraft(currentValue)
  }, [currentValue])

  const dirty = useMemo(
    () => !valuesEqual(draft, currentValue),
    [draft, currentValue],
  )

  const saving = setSetting.isPending
  const error = setSetting.isError
    ? setSetting.error.message || 'Save failed.'
    : null

  const save = useCallback(
    (value: unknown) => {
      setSetting.mutate(
        { daemon, key: def.key, value },
        {
          onSuccess: () => {
            setDraft(value)
          },
        },
      )
    },
    [daemon, def.key, setSetting],
  )

  const toggleHistory = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  return (
    <li className="cfg-row">
      <div className="cfg-row__main">
        <label className="cfg-row__label" htmlFor={controlId}>
          {def.label}
        </label>
        <p className="cfg-row__desc">{def.description}</p>
        <p className="cfg-row__meta">
          <span className="cfg-row__key">{def.key}</span>
          <span className="cfg-row__default">
            default: <code>{formatValue(def.default)}</code>
          </span>
        </p>
      </div>
      <div className="cfg-row__control">
        {dirty && <span className="cfg-dirty">unsaved</span>}
        <div className="cfg-row__widget">
          <Widget
            def={def}
            controlId={controlId}
            value={draft}
            onChange={setDraft}
            disabled={saving}
          />
        </div>
        <div className="cfg-row__actions">
          <button
            type="button"
            className="lc-btn lc-btn--primary"
            disabled={!dirty || saving}
            onClick={() => save(draft)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="lc-btn lc-btn--ghost"
            disabled={!dirty || saving}
            onClick={() => {
              setDraft(currentValue)
              setSetting.reset()
            }}>
            Reset
          </button>
          <button
            type="button"
            className="lc-btn lc-btn--ghost"
            onClick={toggleHistory}
            aria-expanded={expanded}>
            {expanded ? 'Hide history' : 'History'}
          </button>
        </div>
        {error !== null && <div className="cfg-row__error">{error}</div>}
      </div>
      {expanded && (
        <div className="cfg-history">
          {history.isPending && (
            <div className="cfg-empty">// loading history…</div>
          )}
          {history.isError && (
            <div className="panel-banner panel-banner--error">
              <b>Could not load history.</b>{' '}
              {history.error.message || 'History load failed.'}
            </div>
          )}
          {history.data && history.data.history.length === 0 && (
            <div className="cfg-empty">
              // no writes yet — value comes from the declared default.
            </div>
          )}
          {history.data && history.data.history.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Set at</th>
                  <th>Operator</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.data.history.map((entry, idx) => (
                  <tr key={entry.setAt + ':' + idx}>
                    <td>
                      <code>{formatValue(entry.value)}</code>
                    </td>
                    <td>{formatTimestamp(entry.setAt)}</td>
                    <td>
                      <code>{shortUuid(entry.setBy)}</code>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="lc-btn lc-btn--ghost"
                        disabled={
                          saving || valuesEqual(entry.value, currentValue)
                        }
                        onClick={() => save(entry.value)}>
                        Revert
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </li>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Widget
////////////////////////////////////////////////////////////////////////////////

type WidgetProps = {
  def: SettingDefinition
  controlId: string
  value: unknown
  onChange: (next: unknown) => void
  disabled: boolean
}

function Widget({
  def,
  controlId,
  value,
  onChange,
  disabled,
}: WidgetProps): JSX.Element {
  switch (def.type) {
    case 'boolean':
      return (
        <label className="lc-switch" htmlFor={controlId}>
          <input
            id={controlId}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
          />
          <span className="lc-switch__thumb" aria-hidden />
          <span className="lc-switch__label">
            {value === true ? 'On' : 'Off'}
          </span>
        </label>
      )
    case 'number':
      return (
        <input
          id={controlId}
          type="number"
          className="lc-input"
          value={typeof value === 'number' ? value : ''}
          disabled={disabled}
          onChange={e => {
            const n = e.target.value === '' ? '' : Number(e.target.value)
            // Leave the empty string as-is locally so the user can
            // clear the field mid-edit; the save button stays
            // disabled until they type a real number (dirty check
            // against currentValue will be true if currentValue is a
            // number).
            onChange(n)
          }}
        />
      )
    case 'string':
      return (
        <input
          id={controlId}
          type="text"
          className="lc-input"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'enum':
      return (
        <select
          id={controlId}
          className="lc-input"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}>
          {(def.enumValues ?? []).map(v => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      )
    case 'json':
      return (
        <textarea
          id={controlId}
          className="lc-input lc-input--json"
          rows={4}
          value={
            typeof value === 'string' ? value : JSON.stringify(value, null, 2)
          }
          disabled={disabled}
          onChange={e => {
            // Keep raw text on every keystroke; parse only on save —
            // means we accept mid-edit invalid JSON without losing
            // the user's draft. The save button will get a parse
            // error from the daemon if the final value is bad.
            try {
              onChange(JSON.parse(e.target.value))
            } catch {
              onChange(e.target.value)
            }
          }}
        />
      )
  }
}

////////////////////////////////////////////////////////////////////////////////
// Helpers
////////////////////////////////////////////////////////////////////////////////

function valuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function formatValue(v: unknown): string {
  if (v === null) return 'null'
  if (typeof v === 'string') return JSON.stringify(v)
  return JSON.stringify(v)
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function shortUuid(uuid: string): string {
  return uuid.slice(0, 8) + '…'
}
